import * as React from "react";
import {
    getKijkGenres,
    getKijkEpisodes,
    KijkEpisode,
} from "core/services/site/project";
import { useSessionContext } from "ui/context/SessionContext";
import { FragmentProperties, DropdownOption } from "core/models";

interface HookReturn {
    handleSelectGenre(value: string): void;
    handleSelectEpisode(value: string): void;
    handleChangeOnlySeries(value: boolean): void;
    handleChangeExclusive(value: boolean): void;
    handleSelectType(value: string): void;
    searchEpisodes(query: string): void;
    exportProperties(): FragmentProperties[];
    handleDescription(value: string): void;
    genres: DropdownOption[];
    episodes: DropdownOption[];
    selectedGenre: string | undefined;
    selectedEpisode: string | undefined;
    exclusive: boolean;
    onlySeries: boolean;
    episodesPending: boolean;
    selectedType: string;
    description: string | undefined;
}

const useKijk = (
    talpaKijkKey: string | undefined,
    currentProperties?: FragmentProperties[],
): HookReturn => {
    const sessionContext = useSessionContext();

    const currentGenre =
        currentProperties
            ?.find((prop) => prop.key === "mediagenreid")
            ?.value.toString() || undefined;
    const currentEpisode =
        currentProperties
            ?.find((prop) => prop.key === "mediaguid")
            ?.value.toString() || undefined;
    const currentType =
        currentProperties
            ?.find((prop) => prop.key === "mediatype")
            ?.value.toString() || "clip";
    const currentExclusive =
        currentProperties?.find((prop) => prop.key === "mediaexclusive")
            ?.value === true;
    const currentDescription =
        currentProperties
            ?.find((prop) => prop.key === "mediadescription")
            ?.value.toString() || undefined;

    const [genres, setGenres] = React.useState<DropdownOption[]>([]);
    const [episodes, setEpisodes] = React.useState<DropdownOption[]>([]);
    const [selectedGenre, setSelectedGenre] = React.useState<
        string | undefined
    >(currentGenre);
    const [selectedEpisode, setSelectedEpisode] = React.useState<
        string | undefined
    >(currentEpisode);
    const [exclusive, setExclusive] = React.useState<boolean>(currentExclusive);
    const [onlySeries, setOnlySeries] = React.useState<boolean>(false);
    const [episodesPending, setEpisodesPending] =
        React.useState<boolean>(false);
    const [initialEpisodes, setInitialEpisodes] = React.useState<
        DropdownOption[]
    >([]);
    const [selectedType, setSelectedType] = React.useState<string>(currentType);

    const [description, setDescription] = React.useState<string | undefined>(
        currentDescription,
    );

    const getGenres = async () => {
        if (!sessionContext.session) {
            return;
        }
        if (!talpaKijkKey) {
            throw Error("no publisher key");
        }
        if (!sessionContext.selectedProject?.id) {
            throw Error("no projectId");
        }
        const fetchedGenres = await getKijkGenres(
            sessionContext.session.token,
            talpaKijkKey,
            sessionContext.selectedProject.id,
        );
        if (fetchedGenres.error || !fetchedGenres.value) {
            return;
        }
        const dropdownGeneres: DropdownOption[] = fetchedGenres.value.map(
            (genre) => ({
                id: `${genre.id}_${genre.name}`,
                title: genre.name,
                value: genre.id.toString(),
            }),
        );
        setGenres(dropdownGeneres);
    };
    const fetchEpisodes = React.useCallback(
        async (query = "", guid?: string): Promise<DropdownOption[]> => {
            try {
                if (!sessionContext.session) {
                    throw Error("no session");
                }
                if (!talpaKijkKey) {
                    throw Error("no publisher key");
                }
                if (!sessionContext.selectedProject?.id) {
                    throw Error("no projectId");
                }
                const fetchedEpisodes = await getKijkEpisodes(
                    sessionContext.session.token,
                    talpaKijkKey,
                    onlySeries ? ["SERIES"] : ["EPISODE", "SERIES"],
                    query,
                    guid,
                    sessionContext.selectedProject.id,
                );
                if (fetchedEpisodes.error || !fetchedEpisodes.value) {
                    throw new Error("Error while fetching KijkEpisodes");
                }
                const getTitle = (episode: KijkEpisode): string | undefined => {
                    let { title } = episode;
                    if (!title || title.length === 0) {
                        title = episode.seriestitle;
                    }
                    return title;
                };
                const filterMismatches = (episode: KijkEpisode): boolean => {
                    const title = getTitle(episode);
                    return (
                        title?.toLowerCase().includes(query.toLowerCase()) ||
                        false
                    );
                };
                const dropdownEpisodes = fetchedEpisodes.value
                    .filter(filterMismatches)
                    .map((episode) => {
                        const title = getTitle(episode);
                        const extraInfo =
                            episode.type === "SERIES"
                                ? "SERIES"
                                : `S${episode.seasonNumber}E${episode.tvseasonepisodenumber}`;
                        const episodeDate = new Date(episode.lastpublishdate);
                        return {
                            id: episode.guid,
                            title: `${title} ${extraInfo} (${episodeDate.toLocaleDateString()})`,
                            value: `${episode.type}:${episode.guid}`,
                        };
                    });
                return dropdownEpisodes;
            } catch (err) {
                return [];
            }
        },
        [onlySeries, sessionContext.session?.token, talpaKijkKey],
    );
    const getInitialEpisodes = React.useCallback(async () => {
        setEpisodesPending(true);
        const dropdownEpisodes = await fetchEpisodes(
            "",
            currentEpisode?.split(":")[1] || undefined,
        );
        setEpisodes(dropdownEpisodes);
        setInitialEpisodes(dropdownEpisodes);
        setEpisodesPending(false);
    }, [currentEpisode, fetchEpisodes]);

    const searchEpisodes = React.useCallback(
        async (query: string) => {
            setEpisodesPending(true);
            if (query.length < 2) {
                setEpisodes(initialEpisodes);
                setEpisodesPending(false);
                return;
            }
            const dropdownEpisodes = await fetchEpisodes(query);
            setEpisodes(dropdownEpisodes);
            setEpisodesPending(false);
        },
        [initialEpisodes, fetchEpisodes],
    );

    const handleDescription = (value: string) => {
        setDescription(value);
    };
    const handleSelectGenre = React.useCallback((value: string) => {
        setSelectedGenre(value);
    }, []);
    const handleSelectEpisode = React.useCallback((value: string) => {
        setSelectedEpisode(value);
    }, []);
    const handleChangeOnlySeries = (value: boolean) => {
        setOnlySeries(value);
    };
    const handleChangeExclusive = (value: boolean) => {
        setExclusive(value);
    };
    const handleSelectType = (value: string) => {
        setSelectedType(value);
    };
    const exportProperties = () => {
        const props = [
            {
                key: "mediatype",
                value: selectedType,
            },
            {
                key: "mediaexclusive",
                value: exclusive,
            },
        ];
        if (selectedEpisode) {
            props.push({
                key: "mediaguid",
                value: selectedEpisode,
            });
        }
        if (selectedGenre) {
            props.push({
                key: "mediagenreid",
                value: selectedGenre,
            });
        }
        if (description) {
            props.push({
                key: "mediadescription",
                value: description,
            });
        }
        return props;
    };

    React.useEffect(() => {
        if (!talpaKijkKey) {
            return;
        }
        getInitialEpisodes();
        getGenres();
    }, [talpaKijkKey]);
    return {
        handleSelectGenre,
        handleSelectEpisode,
        handleChangeOnlySeries,
        handleChangeExclusive,
        handleSelectType,
        searchEpisodes,
        exportProperties,
        handleDescription,
        genres,
        episodes,
        selectedGenre,
        selectedEpisode,
        exclusive,
        onlySeries,
        episodesPending,
        selectedType,
        description,
    };
};
export default useKijk;
